import React, { useCallback, useEffect, useRef } from "react";
import { WorkOrderWithAppointments } from "@myapp/contract";
import { useWorkOrderFilters } from "../../../../features/work-order";
import { getErrorMessage } from "../../../../shared/lib/errors/getErrorMessage";
import Loader from "../../../../shared/ui/feedback/Loader/Loader";
import { WorkOrderListItem } from "./WorkOrderListItem";

interface WorkOrderListViewProps {
  items: WorkOrderWithAppointments[];
  isLoading: boolean;
  isFetchingNextPage: boolean;
  hasMore: boolean;
  onLoadMore: () => void;
}

const WorkOrderListView: React.FC<WorkOrderListViewProps> = ({
  items,
  isLoading,
  isFetchingNextPage,
  hasMore,
  onLoadMore,
}) => {
  const sentinelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel || !hasMore) return;

    const root = document.getElementById("scrollableDiv");
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) onLoadMore();
      },
      { root, rootMargin: "200px 0px" },
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, onLoadMore]);

  if (isLoading && !items.length) {
    return (
      <div className="my-10 flex w-full justify-center">
        <Loader />
      </div>
    );
  }

  if (!items.length) {
    return (
      <p className="my-10 w-full text-center font-['PT_Sans'] text-[20px] font-bold text-primary">
        Keine Aufträge für die aktuellen Filter.
      </p>
    );
  }

  return (
    <div className="flex w-full flex-col gap-4">
      {items.map((item) => (
        <WorkOrderListItem
          key={`${item.external.auftragJahr}-${item.external.auftragNr}`}
          item={item}
        />
      ))}
      {hasMore && <div ref={sentinelRef} className="h-1" />}
      {isFetchingNextPage && (
        <div className="my-4 flex w-full justify-center">
          <Loader size="small" />
        </div>
      )}
    </div>
  );
};

export const WorkOrderList: React.FC = () => {
  const { data, error, isLoading, isFetchingNextPage, hasNextPage, fetchNextPage, apiFilters } =
    useWorkOrderFilters();

  const items = data?.items ?? [];
  const filterKey = JSON.stringify(apiFilters);

  useEffect(() => {
    document.getElementById("scrollableDiv")?.scrollTo({ top: 0 });
  }, [filterKey]);

  const handleLoadMore = useCallback(() => {
    if (!hasNextPage || isFetchingNextPage) return;
    void fetchNextPage();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  if (error)
    return (
      <div className="my-2 w-full rounded-md border border-[#E63312] bg-[#FFF3F0] p-3">
        <p className="font-['PT_Sans'] text-[16px] font-bold text-[#E63312]">
          {getErrorMessage(error)}
        </p>
      </div>
    );

  return (
    <WorkOrderListView
      key={filterKey}
      items={items}
      isLoading={isLoading}
      isFetchingNextPage={isFetchingNextPage}
      hasMore={Boolean(hasNextPage)}
      onLoadMore={handleLoadMore}
    />
  );
};
